import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import bgImg from "../../pnai-admin-bg.jpg";
import Logo from "../common/Logo";
import Button from "../common/Button";
import Input from "../common/Input";
import Loading from "../common/Loading";

function AgencyResetPassword() {
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [errorState, setErrorState] = useState<boolean>(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/agency/login");
    }
  }, [token, navigate]);

  const validateForm = () => {
    let newErrors: {
      password?: string;
      confirmPassword?: string;
    } = {};

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{12,}$/;

    // Password validation
    if (!password) {
      newErrors.password = "Password is required";
    } else if (!passwordRegex.test(password)) {
      newErrors.password =
        "Password must be at least 12 characters long and include a combination of uppercase and lowercase letters, numbers, and symbols";
    }

    // Confirm password validation
    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      try {
        await axios.post("/api/agency/resetpassword", {
          token,
          password,
        });

        alert(
          "Password has been reset successfully. Please login with your new password."
        );
        navigate("/agency/login");
      } catch (error) {
        setErrorState(true);
        if (axios.isAxiosError(error)) {
          setErrors({
            overall: error.response?.data.error || "Failed to reset password",
          });
        }
      } finally {
        setLoading(false);
      }
    } else {
      setErrorState(true);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2">
      <div
        className="hidden md:flex relative items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bgImg})` }}
      >
        <div className="absolute inset-0 bg-blue-50/70"></div>
        <div className="w-3/5 z-10">
          <Logo className="w-full h-auto" />
        </div>
      </div>
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-base-200">
        <div className="bg-base-100 p-8 rounded-xl shadow-md w-full max-w-sm">
          <h1 className="text-3xl mb-4 font-semibold">Set new password</h1>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <p className="font-semibold pb-1 text-sm">Password</p>
              <Input
                type="password"
                id="password"
                value={password}
                placeholder="Enter new password"
                onChange={(e) => {
                  errorState && setErrorState(false);
                  setPassword(e.target.value);
                }}
                className={`input w-full ${errorState && "border-red-500"}`}
              />
              {errors.password && errorState && (
                <p className="mt-1 text-xs text-red-500">{errors.password}</p>
              )}
            </div>
            <div>
              <p className="font-semibold pb-1 text-sm">Confirm Password</p>
              <Input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                placeholder="Confirm new password"
                onChange={(e) => {
                  errorState && setErrorState(false);
                  setConfirmPassword(e.target.value);
                }}
                className={`input w-full ${errorState && "border-red-500"}`}
              />
              {errors.confirmPassword && errorState && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.confirmPassword}
                </p>
              )}
              {errors.overall && errorState && (
                <p className="mt-1 text-xs text-red-500">{errors.overall}</p>
              )}
            </div>
            <div>
              <Button className="w-full" variant="primary" type="submit">
                Reset Password
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AgencyResetPassword;
